import React from 'react';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import { Box, Chip, Tooltip, Typography } from '@mui/material';

import type { AttentionDisposition, EraTriage } from '../../../store';
import type {
  EncounterWorkDisposition,
  LastPayerStatus,
  WorkReturnedReason,
} from '../../../types/encounterWork';
import { formatDateOnly } from '../../../utils';

import { getLaneColor, getLaneLabel } from './attentionLane';

interface AttentionPulseDotProps {
  triage: EraTriage;
  size?: number;
}

function formatSnoozeDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface AttentionDispositionChipProps {
  disposition: AttentionDisposition;
}

/**
 * Pill that describes the user's current disposition on a triaged claim.
 * Returns null when the disposition is effectively inactive (expired snooze, empty).
 */
export const AttentionDispositionChip: React.FC<AttentionDispositionChipProps> = ({ disposition }) => {
  if (disposition.dismissed_at) {
    return (
      <Chip
        label="Dismissed"
        size="small"
        sx={{
          height: 20,
          fontSize: '0.6875rem',
          fontWeight: 500,
          bgcolor: 'grey.100',
          color: 'text.disabled',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  }
  if (disposition.snoozed_until && new Date(disposition.snoozed_until) > new Date()) {
    return (
      <Chip
        icon={<ScheduleIcon sx={{ fontSize: 12, ml: '4px !important', mr: '-2px !important' }} />}
        label={`Snoozed until ${formatSnoozeDate(disposition.snoozed_until)}`}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.6875rem',
          fontWeight: 500,
          bgcolor: '#FFFBEB',
          color: '#92400E',
          '& .MuiChip-icon': { color: '#92400E' },
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  }
  return null;
};

/* The encounter-level disposition chip (GDN-3381), the successor to the
   claim-keyed "Dismissed"/"Snoozed" pill above. It lives here rather than in
   a new file so the two treatments stay one design decision while the legacy
   claim attention blob is still read by LegacyClaimsTab; retire
   AttentionDispositionChip when that last caller goes. */

const CHIP_SX = {
  height: 20,
  fontSize: '0.6875rem',
  fontWeight: 500,
  '& .MuiChip-label': { px: 0.75 },
} as const;

const DISPOSITION_CHIP_COLORS = {
  archived: { bgcolor: 'grey.100', color: 'text.disabled' },
  snoozed: { bgcolor: '#FFFBEB', color: '#92400E' },
  returned: { bgcolor: '#FEF2F2', color: '#991B1B' },
} as const;

/**
 * The lifecycle event kinds, said the way a biller says them. The wire values
 * name the EVENT ("rejection"), and a caption reading "rejection Sep 2" is
 * the schema talking rather than the product; an unmapped kind falls through
 * to the reason label rather than printing a raw token.
 */
const PAYER_EVENT_LABELS: Record<string, string> = {
  acceptance: 'accepted',
  accepted: 'accepted',
  acknowledged: 'acknowledged',
  acknowledgement: 'acknowledged',
  acknowledgment: 'acknowledged',
  denial: 'denied',
  denied: 'denied',
  paid: 'paid',
  payment: 'paid',
  received: 'received',
  rejected: 'rejected',
  rejection: 'rejected',
  reversal: 'reversed',
  reversed: 'reversed',
};

/** A short "why it came back" for the rare returns with no payer event. */
const RETURNED_REASON_LABELS: Record<WorkReturnedReason, string> = {
  lifecycle_event: 'payer update',
  new_claim: 'new claim',
  snooze_expired: 'snooze ended',
};

/**
 * "Sep 3", through the list's own date helper.
 *
 * The caption sits in a row whose Date column reads "Aug 12"; a reader
 * comparing the two should not have to notice that one is day-first. It
 * also handles a payer event's bare calendar day, which parsed as an
 * instant would become UTC midnight and read a day early west of
 * Greenwich.
 */
const shortDate = (value?: string | null): string => formatDateOnly(value, undefined, '');

export interface DispositionChipProps {
  disposition?: EncounterWorkDisposition | null;
  /** Set while a non-active disposition no longer hides the row. */
  returnedReason?: WorkReturnedReason | null;
  /** The payer event that returned the row, when that is why it came back. */
  lastPayerStatus?: LastPayerStatus | null;
}

/**
 * What the disposition system is currently saying about a row: Archived,
 * Snoozed, or Returned, each with the one caption a biller needs to decide
 * whether to trust it. Renders nothing when there is nothing to say, so the
 * cell is BLANK rather than filled with a placeholder.
 */
export const DispositionChip: React.FC<DispositionChipProps> = ({
  disposition,
  returnedReason,
  lastPayerStatus,
}) => {
  /* Returned wins: the row is back in the queue, which is the fact that
     changes what the biller does next. The underlying state (archived or
     snoozed) is history at that point. */
  const kind = returnedReason
    ? 'returned'
    : disposition?.state === 'archived'
      ? 'archived'
      : disposition?.state === 'snoozed'
        ? 'snoozed'
        : null;
  if (!kind) return null;

  const actor = disposition?.actor_name ?? '';
  const caption =
    kind === 'returned'
      ? [
          (lastPayerStatus?.kind ? PAYER_EVENT_LABELS[lastPayerStatus.kind] : undefined) ??
            (returnedReason ? RETURNED_REASON_LABELS[returnedReason] : ''),
          shortDate(lastPayerStatus?.occurred_at ?? disposition?.updated_at),
        ]
          .filter(Boolean)
          .join(' ')
      : kind === 'snoozed'
        ? [`until ${shortDate(disposition?.hidden_until)}`.trim(), actor].filter(Boolean).join(' · ')
        : [shortDate(disposition?.updated_at), actor].filter(Boolean).join(' · ');

  return (
    <Box sx={{ minWidth: 0 }}>
      <Chip
        label={kind === 'returned' ? 'Returned' : kind === 'snoozed' ? 'Snoozed' : 'Archived'}
        size="small"
        sx={{ ...CHIP_SX, ...DISPOSITION_CHIP_COLORS[kind] }}
      />
      {caption && (
        <Typography color="text.secondary" noWrap sx={{ display: 'block' }} variant="caption">
          {caption}
        </Typography>
      )}
    </Box>
  );
};

export const AttentionPulseDot: React.FC<AttentionPulseDotProps> = ({ triage, size = 8 }) => {
  const color = getLaneColor(triage.attention_lane);
  const label = getLaneLabel(triage.attention_lane);
  const signals = triage.signals?.length ? ` · ${triage.signals.join(', ')}` : '';
  const tooltipText = `${label}${signals}`;

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
};
